import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Select from '@mui/material/Select';
import { IoClose as Close } from 'react-icons/io5';
import { MdPerson as PersonIcon, MdDynamicForm as FieldIcon, MdLabel as LabelIcon } from 'react-icons/md';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import './AddLabelRuleModal.scss';

const cn = 'addLabelRuleModal';

export default function AddLabelRuleModal({
    open,
    onClose,
    onSave,
    title,
    rule,
    onRuleChange,
    onFieldChange,
    onLabelChange,
    onDurationChange,
    validationError,
    ruleTypes,
    conditions,
    comparisonOperators,
    property,
    fieldParameter,
    formFieldList,
    filteredLabelList,
    labelSearchTerm,
    setLabelSearchTerm,
    getAvailableConditions,
    saveDisabled,
}) {
    if (!rule) return null;

    const getEmailPlaceholder = (condition) => {
        if (condition === 'DOMAIN') return 'example.com';
        if (condition === 'CONTAINS') return 'gmail  (any substring)';
        return 'user@example.com';
    };

    const getEmailHint = (condition) => {
        if (condition === 'EQUALS' || condition === 'NOT EQUALS') return 'Must be a full email address';
        if (condition === 'CONTAINS') return 'Any substring — e.g. "gmail" matches all Gmail addresses';
        if (condition === 'DOMAIN') return 'Domain only — no @ symbol (e.g. example.com)';
        return null;
    };

    const renderConditionChips = (condList) => (
        <div className={`${cn}__formGroup`}>
            <label className={`${cn}__label`}>Condition <span className={`${cn}__required`}>*</span></label>
            <div className={`${cn}__chipGroup`}>
                {condList.map((cond, idx) => (
                    <button
                        key={idx}
                        className={`${cn}__chip${rule.condition === cond ? ` ${cn}__chip--active` : ''}`}
                        onClick={() => onRuleChange('condition', cond)}>
                        {cond}
                    </button>
                ))}
            </div>
        </div>
    );

    const renderValueTextField = (placeholder, type = 'text', hint = null) => (
        <div className={`${cn}__formGroup`}>
            <label className={`${cn}__label`}>Value <span className={`${cn}__required`}>*</span></label>
            <TextField
                fullWidth
                type={type}
                size='small'
                value={rule.paramValue}
                onChange={(e) => onRuleChange('paramValue', e.target.value)}
                placeholder={placeholder}
                error={!!validationError}
                className={`${cn}__textField`}
            />
            {hint && !validationError && (
                <span className={`${cn}__hint`}>{hint}</span>
            )}
            {validationError && (
                <span className={`${cn}__validationError`}>{validationError}</span>
            )}
        </div>
    );

    const renderDurationFields = () => (
        <div className={`${cn}__formGroup`}>
            <label className={`${cn}__label`}>Duration <span className={`${cn}__required`}>*</span></label>
            <div className={`${cn}__durationGrid`}>
                {[
                    { key: 'days',    label: 'Days',    min: 0 },
                    { key: 'hours',   label: 'Hours',   min: 0, max: 24 },
                    { key: 'minutes', label: 'Minutes', min: 0, max: 60 },
                    { key: 'seconds', label: 'Seconds', min: 0, max: 60 },
                ].map(({ key, label, min, max }) => (
                    <div key={key} className={`${cn}__durationCell`}>
                        <span className={`${cn}__durationCell__label`}>{label}</span>
                        <TextField
                            fullWidth
                            type='number'
                            size='small'
                            value={rule.paramValue?.[key] || 0}
                            onChange={(e) => onDurationChange(key, e.target.value)}
                            inputProps={{ min, ...(max !== undefined ? { max } : {}) }}
                            className={`${cn}__textField`}
                        />
                    </div>
                ))}
            </div>
        </div>
    );

    const renderLabelSelect = () => (
        <div className={`${cn}__formGroup`}>
            <label className={`${cn}__label`}>Assign Label <span className={`${cn}__required`}>*</span></label>
            <Select
                fullWidth
                size='small'
                value={rule.label?.rn || 0}
                onOpen={() => setLabelSearchTerm('')}
                MenuProps={{ autoFocus: false, MenuListProps: { autoFocusItem: false } }}
                onChange={(e) => onLabelChange(e.target.value)}
                className={`${cn}__select`}>
                <ListSubheader className={`${cn}__searchHeader`}>
                    <TextField
                        size='small'
                        fullWidth
                        placeholder='Search labels…'
                        value={labelSearchTerm}
                        onChange={(e) => { e.stopPropagation(); setLabelSearchTerm(e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        inputProps={{ style: { padding: '8px 4px' } }}
                    />
                </ListSubheader>
                <MenuItem value={0} disabled>Select a label</MenuItem>
                {filteredLabelList.map((label, idx) => (
                    <MenuItem key={idx} value={label?.rn}>
                        <span className={`${cn}__labelItem`}>
                            <LabelIcon className={`${cn}__labelItem__icon`} />
                            {label.name}
                        </span>
                    </MenuItem>
                ))}
            </Select>
        </div>
    );

    const renderReporterSection = () => (
        <>
            <div className={`${cn}__formGroup`}>
                <label className={`${cn}__label`}>Property <span className={`${cn}__required`}>*</span></label>
                <Select
                    fullWidth
                    size='small'
                    value={rule.property}
                    onChange={(e) => {
                        onRuleChange('property', e.target.value);
                        onRuleChange('condition', '');
                        onRuleChange('paramValue', '');
                    }}
                    className={`${cn}__select`}>
                    <MenuItem value="" disabled>Select a property</MenuItem>
                    {property.map((prop, idx) => (
                        <MenuItem key={idx} value={prop}>
                            {prop === 'fullName' ? 'Full Name' :
                             prop === 'phoneNumber' ? 'Phone Number' :
                             prop === 'email' ? 'Email' :
                             prop === 'domain' ? 'Domain' : prop?.toUpperCase()}
                        </MenuItem>
                    ))}
                </Select>
            </div>

            {rule.property && (
                <>
                    {renderConditionChips(getAvailableConditions())}

                    {rule.condition && renderValueTextField(
                        rule.property === 'email' ? getEmailPlaceholder(rule.condition) :
                        rule.property === 'phoneNumber' ? '+1 234 567 8900' : 'Enter value',
                        'text',
                        rule.property === 'email' ? getEmailHint(rule.condition) : null
                    )}

                    {rule.paramValue && !validationError && renderLabelSelect()}
                </>
            )}
        </>
    );

    const renderFieldSection = () => {
        const unsupportedTypes = ['date', 'information', 'file', 'condition'];
        const isUnsupported = unsupportedTypes.includes(rule.property);

        return (
            <>
                <div className={`${cn}__formGroup`}>
                    <label className={`${cn}__label`}>Field <span className={`${cn}__required`}>*</span></label>
                    <Select
                        fullWidth
                        size='small'
                        value={rule.fieldId}
                        onChange={(e) => onFieldChange(e.target.value)}
                        className={`${cn}__select`}>
                        <MenuItem value={0} disabled>Select a field</MenuItem>
                        {fieldParameter.map((field, idx) => (
                            <MenuItem key={idx} value={field.id}>
                                {`${idx + 1}. ${field.title?.toUpperCase()}`}
                            </MenuItem>
                        ))}
                    </Select>
                </div>

                {rule.fieldId && (
                    <>
                        {isUnsupported && (
                            <div className={`${cn}__errorBanner`}>
                                Cannot set rules for <strong>{rule.property}</strong> fields. Please select a different field.
                            </div>
                        )}

                        {(rule.property === 'text' || rule.property === 'phoneNumber') && (
                            <>
                                {renderConditionChips(conditions)}
                                {rule.condition && renderValueTextField(
                                    rule.property === 'phoneNumber' ? '+1 234 567 8900' : 'Enter value'
                                )}
                            </>
                        )}

                        {(rule.property === 'rating' || rule.property === 'number') && (
                            <>
                                {renderConditionChips(comparisonOperators)}
                                {rule.condition && renderValueTextField('Type a number…', 'number')}
                            </>
                        )}

                        {rule.property === 'duration' && (
                            <>
                                {renderConditionChips(comparisonOperators)}
                                {rule.condition && renderDurationFields()}
                            </>
                        )}

                        {rule.property === 'multiChoice' && (
                            <>
                                {renderConditionChips(conditions)}
                                {rule.condition && (
                                    <div className={`${cn}__formGroup`}>
                                        <label className={`${cn}__label`}>Value <span className={`${cn}__required`}>*</span></label>
                                        <Select
                                            fullWidth
                                            size='small'
                                            value={rule.paramValue || 0}
                                            onChange={(e) => onRuleChange('paramValue', e.target.value)}
                                            className={`${cn}__select`}>
                                            <MenuItem value={0} disabled>Select option</MenuItem>
                                            {formFieldList.find(item => item.id === rule.fieldId)?.options?.map((opt, idx) => (
                                                <MenuItem key={idx} value={opt?.id}>
                                                    {opt?.title?.toUpperCase()}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </div>
                                )}
                            </>
                        )}

                        {rule.property === 'multiSelect' && (
                            <>
                                {renderConditionChips(conditions)}
                                {rule.condition && (
                                    <div className={`${cn}__formGroup`}>
                                        <label className={`${cn}__label`}>Values <span className={`${cn}__required`}>*</span></label>
                                        <Select
                                            fullWidth
                                            size='small'
                                            multiple
                                            value={rule.paramValue || []}
                                            onChange={(e) => onRuleChange('paramValue', e.target.value)}
                                            className={`${cn}__select`}>
                                            {formFieldList.find(item => item.id === rule.fieldId)?.options?.map((opt, idx) => (
                                                <MenuItem key={idx} value={opt?.id}>
                                                    {opt?.title?.toUpperCase()}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </div>
                                )}
                            </>
                        )}

                        {rule.paramValue && !isUnsupported && renderLabelSelect()}
                    </>
                )}
            </>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            className={`${cn}__dialog`}>

            <DialogTitle className={`${cn}__title`}>
                <span className={`${cn}__title__text`}>{title}</span>
                <button className={`${cn}__title__close`} onClick={onClose} aria-label='Close'>
                    <Close />
                </button>
            </DialogTitle>

            <DialogContent className={`${cn}__content`}>
                <div className={`${cn}__form`}>
                    {/* Rule Type Toggle */}
                    <div className={`${cn}__formGroup`}>
                        <label className={`${cn}__label`}>Rule Type <span className={`${cn}__required`}>*</span></label>
                        <div className={`${cn}__typeToggle`}>
                            {ruleTypes.map((type, idx) => (
                                <button
                                    key={idx}
                                    className={`${cn}__typeBtn${rule.type === type ? ` ${cn}__typeBtn--active` : ''}`}
                                    onClick={() => onRuleChange('type', type)}>
                                    <span className={`${cn}__typeBtn__icon`}>
                                        {type === 'reporter' ? <PersonIcon /> : <FieldIcon />}
                                    </span>
                                    {type === 'reporter' ? 'Reporter' : 'Field'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={`${cn}__divider`} />

                    {rule.type === 'reporter' && renderReporterSection()}
                    {rule.type === 'field' && renderFieldSection()}
                </div>
            </DialogContent>

            <DialogActions className={`${cn}__actions`}>
                <button className={`${cn}__cancelBtn`} onClick={onClose}>
                    Cancel
                </button>
                <BorderBlueButtonV2
                    text={title === 'Edit Rule' ? 'Update Rule' : 'Add Rule'}
                    onClick={() => onSave(rule)}
                    disabled={saveDisabled}
                />
            </DialogActions>
        </Dialog>
    );
}
